import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type ActionMenuItem, type SyutGridColumn } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { propertyService } from 'api/services/propertyService';
import { documentSeriesService } from 'api/services/documentSeriesService';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import {
  documentTypeLabel,
  runningSeriesLabel,
  type DocumentSeries,
  type PendingNumbersSummary,
  type RunningSeries,
} from 'api/types/documentSeries.types';

/**
 * ============================================================================
 * DOCUMENT SERIES — statutory numbering (PMS Phase 2 slice 4)
 * ============================================================================
 * One series per property × document type × financial year: tax invoice, credit
 * note, receipt voucher, refund voucher. The year is the property's accounting
 * entity's financial year; a new year's series starts at 1 with the previous
 * year's prefix. Numbers are issued without gaps when the document is saved —
 * the prefix and digits can change only until the first number of the year.
 * A property not linked to an accounting entity cannot number: its receipts wait
 * as "pending" and are numbered here once it is linked.
 *
 * Running series (GRC-1) sit below: numbers that are not tax documents — the guest
 * registration card. One per property, no year reset; prefix (may be empty), digits
 * (0 = no padding) and a start number, so a hotel can continue its paper pad. All
 * three are fixed once the first number is issued.
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const MAX_LENGTH = 16;

const DocumentSeriesScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const canEdit = allowedActions.includes('edit');
  const canSubmit = allowedActions.includes('submit');

  const { defaultPropertyId } = useDefaultProperty();
  const [propertyId, setPropertyId] = useState('');
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);

  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const [rows, setRows] = useState<DocumentSeries[]>([]);
  const [running, setRunning] = useState<RunningSeries[]>([]);
  const [pending, setPending] = useState<PendingNumbersSummary | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [businessDate, setBusinessDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) { setRows([]); setRunning([]); setPending(null); setBlocked(null); setBusinessDate(null); return; }
    setLoading(true);
    try {
      // Admins create the current year's series (and the running series) on sight; everyone else just reads.
      const reason = canEdit ? await documentSeriesService.prepare(propertyId) : '';
      if (canEdit) await documentSeriesService.runningPrepare(propertyId);
      const [list, run, pend, bd] = await Promise.all([
        documentSeriesService.list(propertyId),
        documentSeriesService.runningList(propertyId),
        documentSeriesService.pending(propertyId),
        documentSeriesService.businessDate(propertyId).catch(() => null),
      ]);
      setRows(list);
      setRunning(run);
      setPending(pend);
      setBlocked(reason || pend.blockingReason || null);
      setBusinessDate(bd);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load document series.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, canEdit]);

  useEffect(() => { void load(); }, [load]);

  const issuePending = useCallback(async () => {
    if (!propertyId) return;
    setIssuing(true);
    try {
      const r = await documentSeriesService.issuePending(propertyId);
      if (r.stillPending > 0) notificationService.toast.warning(`${r.issued} numbered; ${r.stillPending} still pending — ${r.reason ?? ''}`);
      else notificationService.toast.success(`${r.issued} pending receipt number(s) issued.`);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Issuing pending numbers failed.');
    } finally {
      setIssuing(false);
    }
  }, [propertyId, load]);

  // ---- edit prefix / digits ---------------------------------------------------------------
  const [editing, setEditing] = useState<DocumentSeries | null>(null);
  const [prefix, setPrefix] = useState('');
  const [width, setWidth] = useState(5);
  const [saving, setSaving] = useState(false);
  const openEdit = useCallback((s: DocumentSeries) => { setEditing(s); setPrefix(s.prefix); setWidth(s.sequenceWidth); }, []);
  const preview = editing ? `${prefix || '?'}/${editing.fyToken}/${'1'.padStart(width, '0')}` : '';
  const tooLong = preview.length > MAX_LENGTH;
  const badShape = !!prefix && !/^[A-Z0-9][A-Z0-9-]{0,9}$/.test(prefix);

  const saveEdit = useCallback(async () => {
    if (!editing || tooLong || badShape || !prefix) return;
    setSaving(true);
    try {
      await documentSeriesService.update(editing.id, { prefix, sequenceWidth: width, rowVersion: editing.rowVersion });
      notificationService.toast.success(`${documentTypeLabel(editing.documentType)} series updated.`);
      setEditing(null);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  }, [editing, tooLong, badShape, prefix, width, load]);

  const checkGaps = useCallback(async (s: DocumentSeries) => {
    try {
      const g = await documentSeriesService.gaps(s.id);
      if (g.missingSequences.length === 0)
        notificationService.toast.success(`No gaps: ${g.issuedCount} number(s) issued, 1 to ${g.lastNumber}, each with its document.`);
      else
        notificationService.toast.error(`Missing sequence number(s): ${g.missingSequences.slice(0, 20).join(', ')}${g.missingSequences.length > 20 ? '…' : ''}`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Gap check failed.');
    }
  }, []);

  // ---- running series: prefix / digits / start number ------------------------------------
  const [runEditing, setRunEditing] = useState<RunningSeries | null>(null);
  const [runPrefix, setRunPrefix] = useState('');
  const [runWidth, setRunWidth] = useState(0);
  const [runStart, setRunStart] = useState('1');
  const openRunEdit = useCallback((s: RunningSeries) => {
    setRunEditing(s); setRunPrefix(s.prefix); setRunWidth(s.sequenceWidth); setRunStart(String(s.startNumber));
  }, []);
  const runStartNumber = Number(runStart);
  const runStartBad = !Number.isInteger(runStartNumber) || runStartNumber < 1;
  const runPrefixBad = !/^[A-Z0-9/-]{0,20}$/.test(runPrefix);
  const runPreview = runStartBad ? '' : `${runPrefix}${runWidth > 0 ? String(runStartNumber).padStart(runWidth, '0') : runStartNumber}`;

  const saveRunEdit = useCallback(async () => {
    if (!runEditing || runStartBad || runPrefixBad) return;
    setSaving(true);
    try {
      await documentSeriesService.runningUpdate(runEditing.id, {
        prefix: runPrefix, sequenceWidth: runWidth, startNumber: runStartNumber, rowVersion: runEditing.rowVersion,
      });
      notificationService.toast.success(`${runningSeriesLabel(runEditing.seriesType)} numbers start at ${runPreview}.`);
      setRunEditing(null);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  }, [runEditing, runStartBad, runPrefixBad, runPrefix, runWidth, runStartNumber, runPreview, load]);

  const runningColumns = useMemo<SyutGridColumn<RunningSeries>[]>(() => [
    { id: 'seriesType', header: 'Number', accessorKey: 'seriesType', size: 190, cell: ({ row }) => runningSeriesLabel(row.original.seriesType) },
    { id: 'prefix', header: 'Prefix', accessorKey: 'prefix', size: 120,
      cell: ({ row }) => row.original.prefix
        ? <span style={{ fontFamily: 'monospace' }}>{row.original.prefix}</span>
        : <span style={{ color: 'var(--text-muted)' }}>none</span> },
    { id: 'sequenceWidth', header: 'Digits', accessorKey: 'sequenceWidth', size: 90,
      cell: ({ row }) => row.original.sequenceWidth || <span style={{ color: 'var(--text-muted)' }}>no padding</span> },
    { id: 'startNumber', header: 'Start number', accessorKey: 'startNumber', size: 110 },
    { id: 'issuedCount', header: 'Issued', accessorKey: 'issuedCount', size: 80 },
    { id: 'nextNumber', header: 'Next number', accessorKey: 'nextNumber', size: 170, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.nextNumber}</span> },
    { id: 'updatedByName', header: 'Updated by', accessorKey: 'updatedByName', size: 140, cell: ({ row }) => row.original.updatedByName || '—' },
  ], []);

  const getRunningRowActions = useCallback((s: RunningSeries): ActionMenuItem[] =>
    canEdit && s.issuedCount === 0
      ? [{ id: 'edit', label: 'Edit numbering', icon: <IconSystem.edit size={18} />, onClick: () => openRunEdit(s) }]
      : [], [canEdit, openRunEdit]);

  // ---- grid ---------------------------------------------------------------------------------
  const columns = useMemo<SyutGridColumn<DocumentSeries>[]>(() => [
    { id: 'documentType', header: 'Document', accessorKey: 'documentType', size: 160, cell: ({ row }) => documentTypeLabel(row.original.documentType) },
    { id: 'fyCode', header: 'Financial year', accessorKey: 'fyCode', size: 130, cell: ({ row }) => row.original.fyCode || row.original.fyToken },
    { id: 'prefix', header: 'Prefix', accessorKey: 'prefix', size: 90, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.prefix}</span> },
    { id: 'sequenceWidth', header: 'Digits', accessorKey: 'sequenceWidth', size: 70 },
    { id: 'lastNumber', header: 'Issued', accessorKey: 'lastNumber', size: 80 },
    { id: 'nextNumber', header: 'Next number', accessorKey: 'nextNumber', size: 170, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.nextNumber}</span> },
    { id: 'entityName', header: 'Accounting entity', accessorKey: 'entityName', size: 200, cell: ({ row }) => row.original.entityName || '—' },
    { id: 'updatedByName', header: 'Updated by', accessorKey: 'updatedByName', size: 140, cell: ({ row }) => row.original.updatedByName || '—' },
  ], []);

  const getRowActions = useCallback((s: DocumentSeries): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (canEdit && s.lastNumber === 0) items.push({ id: 'edit', label: 'Edit prefix', icon: <IconSystem.edit size={18} />, onClick: () => openEdit(s) });
    items.push({ id: 'gaps', label: 'Check for gaps', icon: <IconSystem.check size={18} />, onClick: () => { void checkGaps(s); } });
    return items;
  }, [canEdit, openEdit, checkGaps]);

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(() => { void load(); })];

  return (
    <EnterpriseScreenShell
      title="Document Series"
      description="Numbering of tax invoices, credit notes, receipt and refund vouchers (per property and financial year, without gaps) and of registration cards."
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ minWidth: 280 }}>
          <SyutSelect
            label="Property"
            isAsync
            loadOptions={loadPropertyOptions}
            defaultOptions
            cacheOptions={false}
            value={propertyId}
            onChange={(v) => setPropertyId((v as string) || '')}
            placeholder="Select property..."
            size="sm"
            menuPortalTarget={document.body}
          />
        </div>
        {businessDate && (
          <span className="badge badge-info" style={{ marginBottom: '0.4rem' }} title="Local date-time minus the property's business-day cutoff">
            Business date {businessDate}
          </span>
        )}
      </div>

      {blocked && (
        <div style={{
          padding: '0.6rem 0.8rem', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.85rem',
          background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)',
        }}>
          {blocked} Documents are still saved; their numbers wait until this is fixed.
        </div>
      )}
      {pending && pending.pendingCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          padding: '0.6rem 0.8rem', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.85rem',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
        }}>
          <span><b>{pending.pendingCount}</b> receipt(s) waiting for a number.</span>
          {canSubmit && (
            <button className="btn btn-primary btn-sm" onClick={issuePending} disabled={issuing || !!pending.blockingReason}
              title={pending.blockingReason ?? 'Numbers them in date order'}>
              {issuing ? 'Issuing…' : 'Issue pending numbers'}
            </button>
          )}
        </div>
      )}

      <div className="data-grid-container">
        <SyutGrid
          data={rows}
          columns={columns}
          isLoading={loading}
          rowActions={getRowActions}
          emptyMessage={propertyId ? 'No series yet for this property' : 'Select a property'}
        />
      </div>

      <div style={{ marginTop: '1.25rem', marginBottom: '0.5rem' }}>
        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Running series (not tax documents)</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          One per property and never reset by year. To continue a paper registration-card pad, clear the prefix and set the
          pad&apos;s next unused number as the start. Numbers already typed in at check-in are skipped.
        </div>
      </div>
      <div className="data-grid-container">
        <SyutGrid
          data={running}
          columns={runningColumns}
          isLoading={loading}
          rowActions={getRunningRowActions}
          emptyMessage={propertyId ? 'Created with the first check-in, or when an administrator opens this screen' : 'Select a property'}
        />
      </div>

      {runEditing && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-sm">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{runningSeriesLabel(runEditing.seriesType)} numbers</h2>
              <button className="modal-close" onClick={() => setRunEditing(null)} disabled={saving}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label" htmlFor="run-prefix">Prefix</label>
                  <input id="run-prefix" type="text" className={`form-input${runPrefixBad ? ' form-field-error' : ''}`} value={runPrefix} maxLength={20}
                    placeholder="None" onChange={(e) => setRunPrefix(e.target.value.toUpperCase().replace(/\s/g, ''))} style={{ fontFamily: 'monospace' }} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="run-width">Digits</label>
                  <input id="run-width" type="number" className="form-input" value={runWidth} min={0} max={8}
                    onChange={(e) => setRunWidth(Math.min(8, Math.max(0, Number(e.target.value) || 0)))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="run-start">Start number <span className="text-danger">*</span></label>
                <input id="run-start" type="number" className={`form-input${runStartBad ? ' form-field-error' : ''}`} value={runStart} min={1}
                  onChange={(e) => setRunStart(e.target.value)} />
              </div>
              {!runStartBad && (
                <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  First number: <b style={{ fontFamily: 'monospace' }}>{runPreview}</b>
                </div>
              )}
              {runPrefixBad && <div className="form-error-message">Capital letters, digits, - and / only (max 20). Leave it empty for a bare number.</div>}
              {runStartBad && <div className="form-error-message">Give a whole number of 1 or more.</div>}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                Digits 0 = no padding. The start must be after any number this property already has in the same form.
                Fixed once the first number is issued.
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setRunEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={saveRunEdit} disabled={saving || runStartBad || runPrefixBad}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}

      {editing && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-sm">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{documentTypeLabel(editing.documentType)} — {editing.fyCode || editing.fyToken}</h2>
              <button className="modal-close" onClick={() => setEditing(null)} disabled={saving}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Prefix <span className="text-danger">*</span></label>
                  <input type="text" className={`form-input${badShape ? ' form-field-error' : ''}`} value={prefix} maxLength={10}
                    onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/\s/g, ''))} style={{ fontFamily: 'monospace' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Digits</label>
                  <input type="number" className="form-input" value={width} min={3} max={8}
                    onChange={(e) => setWidth(Math.min(8, Math.max(3, Number(e.target.value) || 5)))} />
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                First number: <b style={{ fontFamily: 'monospace' }}>{preview}</b>{' '}
                <span style={{ color: tooLong ? 'var(--danger, #dc2626)' : 'var(--text-muted)' }}>({preview.length}/{MAX_LENGTH} characters)</span>
              </div>
              {badShape && <div className="form-error-message">Capital letters, digits and '-' only, starting with a letter or digit.</div>}
              {tooLong && <div className="form-error-message">GST allows at most 16 characters — shorten the prefix or the digits.</div>}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                Fixed once the first number of the year is issued. Each property of an accounting entity needs its own prefix.
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={saving || tooLong || badShape || !prefix}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}
    </EnterpriseScreenShell>
  );
};

export default DocumentSeriesScreen;
